import { getMetaTrackingData } from '../shared/tracking'
import { getUser } from '../api'
import store from '../store'
import { RESURRECTION_COUPON_CODES } from 'src/constants/resurrection-constants'

async function getUserTrackingData() {
  try {
    // Try to get user data from store first
    const state = store.getState()
    let user = state?.user?.info || {}
    const defaultAddress = state?.api?.subscription?.info?.addresses?.[0] || {}

    // If store data is not available, fetch from API
    if (!user.email) {
      const { data } = await getUser()
      user = data?.user || {}
    }

    // Clean phone number
    const cleanPhone = user.phone?.replace(/[^\d]/g, '') || null

    return {
      email: user.email || null,
      phone_number: cleanPhone,
      first_name: user.firstname || null,
      last_name: user.lastname || null,
      zip_code: defaultAddress.postcode || user.zipcode || null,
      date_of_birth: user.birthdate || null,
      city: defaultAddress.city || null,
      state: defaultAddress.region || null
    }
  } catch (error) {
    console.error('Error getting user tracking data:', error)
    return {}
  }
}

function getPrepaidOfferData(prepaidOfferId, resurrectionCampaign) {
  if (!prepaidOfferId && !resurrectionCampaign?.prepaidCampaign) {
    return {}
  }

  return {
    prepaid_offer_id: prepaidOfferId || resurrectionCampaign?.prepaidCampaign
  }
}

function cleanTrackingData(data) {
  return Object.fromEntries(
    Object.entries(data).filter(([, value]) => value != null)
  )
}

function getCampaignData(resurrectionCampaign) {
  if (!resurrectionCampaign) {
    return {}
  }

  return {
    resurrection_campaign_id: resurrectionCampaign.id || null,
    resurrection_chef_id: resurrectionCampaign.chefId || null,
    resurrection_chef_brand_id: resurrectionCampaign.chefBrandId || null,
    resurrection_chef_brand_ids: resurrectionCampaign.chefBrandIds || [],
    resurrection_highlighted_meals:
      resurrectionCampaign.highlightedMealsIds || [],
    resurrection_prepaid_campaign: resurrectionCampaign.prepaidCampaign || null
  }
}

function getCouponData(resurrectionCampaign, customCouponCode) {
  const couponCode = customCouponCode || resurrectionCampaign?.couponCode
  if (!couponCode) {
    return {}
  }

  return {
    coupon_code: couponCode,
    is_resurrection_coupon:
      couponCode !== RESURRECTION_COUPON_CODES.CREDITS_35X4
  }
}

function getUserData(subscriptionData, userStore) {
  // Ensure number conversion happens correctly
  const resurrectionCount = Number(subscriptionData.resurrectionCount)
  const validResurrectionCount = isNaN(resurrectionCount)
    ? 0
    : resurrectionCount

  // Format lastResurrectionDate correctly
  const lastResurrectionDateISO = subscriptionData.lastResurrectionDate
    ? new Date(subscriptionData.lastResurrectionDate).toISOString()
    : null

  return {
    subscriber_id: subscriptionData.userId,
    // Use nullish coalescing to prioritize subscriptionData.email
    email: subscriptionData.email ?? userStore?.email ?? null,
    resurrection_count: validResurrectionCount,
    last_resurrection_date: lastResurrectionDateISO,
    is_reactivation: true,
    timestamp: new Date().toISOString(),
    context: 'subscription',
    menu_store: userStore?.name || 'unknown'
  }
}

function getUtmParams() {
  const search = new URLSearchParams(window.location.search)
  const utms = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content']
  return Object.fromEntries(
    utms.map(key => [key, search.get(key)]).filter(([, value]) => value != null)
  )
}

interface SubscriptionData {
  userId?: string
  email?: string | null
  resurrectionCount?: number | string
  lastResurrectionDate?: string | null
}

interface ResurrectionCampaignData {
  id?: string
  chefId?: string
  chefBrandId?: string
  chefBrandIds?: string[]
  highlightedMealsIds?: string[]
  prepaidCampaign?: string | null
  couponCode?: string | null
}

export async function getResurrectionTrackingData(
  subscriptionData: SubscriptionData = {},
  resurrectionCampaign?: ResurrectionCampaignData | null,
  couponCode?: string | null,
  prepaidOfferId?: string | null,
  creditsAvailable?: number | null
) {
  try {
    const userStore = store.getState()?.user?.info || {}

    // Get user data fields separately first
    const userDataFromHelper = await getUserTrackingData()

    const trackingData = {
      ...getUtmParams(),
      ...getCampaignData(resurrectionCampaign),
      ...getCouponData(resurrectionCampaign, couponCode),
      // Spread specific fields from helper, excluding email handled by getUserData
      phone_number: userDataFromHelper.phone_number,
      first_name: userDataFromHelper.first_name,
      last_name: userDataFromHelper.last_name,
      zip_code: userDataFromHelper.zip_code,
      date_of_birth: userDataFromHelper.date_of_birth,
      city: userDataFromHelper.city,
      state: userDataFromHelper.state,
      // Spread getUserData AFTER helper fields to ensure its email/dates take precedence
      ...getUserData(subscriptionData, userStore),
      ...getPrepaidOfferData(prepaidOfferId, resurrectionCampaign),
      ...getMetaTrackingData()
    }

    // Add credits if available
    if (creditsAvailable !== null && creditsAvailable !== undefined) {
      trackingData.credits_available = creditsAvailable
    }

    return cleanTrackingData(trackingData)
  } catch (error) {
    console.error('Error getting resurrection tracking data:', error)
    return {}
  }
}
